'use client';

import type { ReactNode } from 'react';
import Link from 'next/link';
import { AppShell } from '@/components/app-shell';
import { useAuth } from '@/lib/use-auth';
import { Button } from '@/components/ui/button';
import { CartDrawer } from '@/lib/cart/cart-drawer';
import { CartFloatingButton } from '@/lib/cart/cart-floating-button';

/**
 * MarketShell — shared chrome for every `/market/*` route (extracted from
 * the original `app/market/layout.tsx`).
 *
 * SPEC-127 P-191 (Sprint 58 UX polish): the layout.tsx was split into a
 * server component (which exports `metadata` for the `<title>`) + this
 * client component (which owns the `useAuth` switch between the
 * authenticated AppShell and the public market header). The split is
 * required because Next.js App Router does not allow `export const
 * metadata` in `'use client'` files — the previous monolithic client
 * layout meant `/market` (the public catalog) inherited the root default
 * title "Profileia — Tu perfil inteligente. La casa del emprendedor.".
 *
 * SPEC-094 §1.1 (Market Público): la ruta `/market` (catálogo) + detalle
 * de producto + tienda vendedor DEBEN ser públicas (sin auth). Solo
 * requieren auth: comprar créditos, comprar producto, dejar review,
 * abrir disputa.
 *
 * Este layout detecta si el usuario está autenticado (vía `useAuth`
 * hook) y renderiza:
 *   - Si autenticado → `AppShell` (sidebar + topbar — consistencia con
 *     el resto de la app autenticada).
 *   - Si NO autenticado → layout público simple (header con logo
 *     Profileia + botones "Iniciar Sesión" + "Comenzar Gratis").
 *
 * Durante el estado `loading` del hook `useAuth` (el primer render del
 * cliente antes de que se resuelva `/api/auth/me`), se renderiza el
 * layout público simple para evitar:
 *   (a) un flash del AppShell autenticado en usuarios no autenticados;
 *   (b) un hydration mismatch (el SSR renderiza sin saber si el user
 *       está autenticado — `useAuth` arranca con `loading: true` en el
 *       servidor + primer render del cliente, así que ambos coinciden
 *       en "public layout").
 *
 * Spec ref: SPEC-094 §1.1, SPEC-081 Fase 1 §6.1 + §6.5 (AppShell pattern),
 *           SPEC-NEGOCIO §8.1 (URLs públicas), SPEC-127 §3 (P-191 —
 *           market metadata).
 *
 * Anti-P-041 (no `as any`): el prop `href` de `Link` es un string literal
 * tipado por Next.js typedRoutes — no se necesita `as Route` para rutas
 * estáticas conocidas.
 * Anti-P-042 (no `console.*`): sin logging.
 * Anti-P-083 (no refs docs en UI): los strings visibles ("Iniciar Sesión",
 * "Comenzar Gratis", "Profileia") NO contienen refs a docs internas.
 */

export default function MarketShell({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  // During SSR + the first client render, `loading === true` (the hook
  // starts in `loading: true` state and only flips to `false` after the
  // `/api/auth/me` fetch resolves on the client). This means the SSR
  // HTML + the first client render both render the public layout — no
  // hydration mismatch. After auth resolves, we re-render with the
  // correct layout.
  //
  // We render AppShell ONLY when we have a confirmed authenticated user
  // (`user` is non-null + `loading` is false). While `loading` is true,
  // we render the public layout — this means authenticated users see a
  // brief flash of the public header before AppShell takes over. That's
  // acceptable UX (the alternative is a full-page spinner, which feels
  // slower for the unauthenticated majority of /market visitors).
  if (user && !loading) {
    return (
      <AppShell>
        {children}
        {/* v136: Cart drawer + floating button available en TODAS las páginas del market */}
        <CartDrawer />
        <CartFloatingButton />
      </AppShell>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Public market header — logo + Login/Sign Up buttons */}
      <header className="sticky top-0 z-40 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex h-14 items-center justify-between px-4">
          <Link
            href="/market"
            className="flex items-center gap-2 text-base font-semibold"
            aria-label="Profileia Market — inicio"
          >
            <span className="text-[#06b6d4]">Profile</span>
            <span className="text-[#8b5cf6]">ia</span>
            <span className="ml-1 text-xs font-normal text-muted-foreground hidden sm:inline">
              · Market
            </span>
          </Link>
          <nav className="flex items-center gap-2" aria-label="Acciones de cuenta">
            <Button asChild variant="ghost" size="sm">
              <Link href="/login">Iniciar Sesión</Link>
            </Button>
            <Button asChild size="sm">
              <Link href="/register">Comenzar Gratis</Link>
            </Button>
          </nav>
        </div>
      </header>

      {/* Page content */}
      <main id="main-content" className="flex-1">
        {children}
      </main>

      {/* v136: Cart drawer + floating button también en layout público */}
      <CartDrawer />
      <CartFloatingButton />

      {/* Public market footer — branding */}
      <footer className="border-t border-border bg-muted/30">
        <div className="container mx-auto px-4 py-6 text-center text-xs text-muted-foreground">
          <p>
            <span className="text-[#06b6d4]">Profile</span>
            <span className="text-[#8b5cf6]">ia</span> — Tu perfil inteligente. La casa del emprendedor.
          </p>
        </div>
      </footer>
    </div>
  );
}
